import { useState, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { ExternalLink, Download, RefreshCw, Copy, Check, Maximize2 } from 'lucide-react';
import { getCategoryConfig } from '@/constants/categories';
import type { Product } from '@/types';
import { useProductActions } from '@/hooks/useProductActions';
import { MockupGenerator } from '@/components/MockupGenerator';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

interface ProductPageProps {
  product: Product;
  onRedoCrop?: () => void;
  onDeleted?: () => void;
  onRenamed?: (product: Product) => void;
  onProductUpdated?: (product: Product) => void;
}

interface LightboxSlide {
  src: string;
  title: string;
  description: string;
}

export function ProductPage({ product, onRedoCrop, onDeleted, onRenamed, onProductUpdated }: ProductPageProps) {
  const [copiedLink, setCopiedLink] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [previewLightboxOpen, setPreviewLightboxOpen] = useState(false);

  const {
    dialogOpen,
    dialogMode,
    newTitle,
    setNewTitle,
    submitting,
    error: actionError,
    openRenameDialog,
    openDeleteDialog,
    closeDialog,
    confirmDialog,
  } = useProductActions({
    onDeleted,
    onRenamed,
  });

  const categoryConfig = getCategoryConfig(product.category);
  const isLandscape = product.orientation === 'landscape';
  const previewImage = product.printFileUrls?.[isLandscape ? '4:3' : '3:4'] || product.masterImageUrl;

  const lightboxImages: LightboxSlide[] = useMemo(() => {
    if (!product.printFileUrls) return [];
    return Object.entries(product.printFileUrls).map(([ratio, url]) => ({
      src: url,
      title: ratio,
      description: `${ratio} aspect ratio`,
    }));
  }, [product.printFileUrls]);

  const handleCopyLink = useCallback(async () => {
    if (product.googleDriveLink) {
      await navigator.clipboard.writeText(product.googleDriveLink);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    }
  }, [product.googleDriveLink]);

  const openLightbox = useCallback((ratio: string) => {
    const index = Object.keys(product.printFileUrls || {}).indexOf(ratio);
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, [product.printFileUrls]);

  const handleMockupsUpdated = useCallback((mockups: string[]) => {
    if (onProductUpdated) {
      onProductUpdated({ ...product, mockupUrls: mockups });
    }
  }, [product, onProductUpdated]);

  const handleOpenRename = useCallback(() => {
    openRenameDialog(product);
  }, [openRenameDialog, product]);

  const handleOpenDelete = useCallback(() => {
    openDeleteDialog(product);
  }, [openDeleteDialog, product]);

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <h2 className="text-3xl font-bold">{product.title}</h2>
          <Badge variant="secondary">{categoryConfig.label}</Badge>
          <Badge variant="outline">{isLandscape ? 'Landscape' : 'Portrait'}</Badge>
        </div>
        <p className="text-muted-foreground">
          Created {product.createdAt.toLocaleDateString()}
        </p>
      </div>

      {isLandscape && (
        <PreviewCard
          variant="landscape"
          previewImage={previewImage}
          productTitle={product.title}
          onOpenLightbox={() => setPreviewLightboxOpen(true)}
        />
      )}

      <div className={`grid gap-6 ${isLandscape ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-1 lg:grid-cols-2'}`}>
        {!isLandscape && (
          <PreviewCard
            variant="portrait"
            previewImage={previewImage}
            productTitle={product.title}
            onOpenLightbox={() => setPreviewLightboxOpen(true)}
          />
        )}

        {isLandscape ? (
          <>
            <CustomerFilesCard
              googleDriveLink={product.googleDriveLink}
              copiedLink={copiedLink}
              onCopyLink={handleCopyLink}
            />
            <DeliverablePdfCard finalPdfUrl={product.finalPdfUrl} />
            <ActionsCard
              onRedoCrop={onRedoCrop}
              hasMasterImage={!!product.masterImageUrl}
              onRename={handleOpenRename}
              onDelete={handleOpenDelete}
              actionError={actionError}
            />
          </>
        ) : (
          <div className="space-y-6">
            <CustomerFilesCard
              googleDriveLink={product.googleDriveLink}
              copiedLink={copiedLink}
              onCopyLink={handleCopyLink}
            />
            <DeliverablePdfCard finalPdfUrl={product.finalPdfUrl} />
            <ActionsCard
              onRedoCrop={onRedoCrop}
              hasMasterImage={!!product.masterImageUrl}
              onRename={handleOpenRename}
              onDelete={handleOpenDelete}
              actionError={actionError}
            />
          </div>
        )}
      </div>

      <MockupGenerator
        productId={product.id}
        existingMockups={product.mockupUrls}
        printFileUrls={product.printFileUrls}
        onMockupsUpdated={handleMockupsUpdated}
      />

      {product.printFileUrls && Object.keys(product.printFileUrls).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Print File Gallery</CardTitle>
            <CardDescription>Click any image to view in full screen</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {Object.entries(product.printFileUrls).map(([ratio, url]) => (
                <div key={ratio} className="space-y-2">
                  <div
                    className={`relative ${isLandscape ? 'aspect-[4/3]' : 'aspect-[3/4]'} bg-muted rounded-lg overflow-hidden border cursor-pointer group`}
                    onClick={() => openLightbox(ratio)}
                  >
                    <img
                      src={url}
                      alt={`${ratio} print`}
                      className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Maximize2 className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium">{ratio}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {previewImage && (
        <Lightbox
          open={previewLightboxOpen}
          close={() => setPreviewLightboxOpen(false)}
          slides={[{ src: previewImage }]}
          styles={{ container: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
          controller={{ closeOnBackdropClick: true }}
        />
      )}

      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={lightboxImages}
        index={lightboxIndex}
        styles={{ container: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
        controller={{ closeOnBackdropClick: true }}
        render={{
          slide: ({ slide }) => (
            <div className="pointer-events-none relative mx-auto flex h-full w-full items-center justify-center">
              <img
                src={(slide as LightboxSlide).src}
                alt={(slide as LightboxSlide).title || 'Print file'}
                className="pointer-events-auto max-h-full max-w-full object-contain"
              />
              <div className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 text-center text-white">
                <span className="inline-flex items-center gap-1 rounded-full bg-black/70 px-3 py-1">
                  <span className="font-semibold text-base">{(slide as LightboxSlide).title}</span>
                  <span className="text-sm font-semibold text-gray-200">aspect ratio</span>
                </span>
              </div>
            </div>
          ),
        }}
      />

      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialogMode === 'delete' ? 'Delete product' : 'Rename product'}
            </DialogTitle>
            <DialogDescription>
              {dialogMode === 'delete'
                ? `Are you sure you want to delete "${product.title}"? This cannot be undone.`
                : 'Update the product title below.'}
            </DialogDescription>
          </DialogHeader>

          {dialogMode === 'rename' && (
            <div className="space-y-2">
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="New product title"
                autoFocus
              />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant={dialogMode === 'delete' ? 'destructive' : 'default'}
              onClick={confirmDialog}
              disabled={submitting || (dialogMode === 'rename' && !newTitle.trim())}
            >
              {dialogMode === 'delete' ? 'Delete' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface PreviewCardProps {
  variant: 'landscape' | 'portrait';
  previewImage: string | null | undefined;
  productTitle: string;
  onOpenLightbox: () => void;
}

function PreviewCard({ variant, previewImage, productTitle, onOpenLightbox }: PreviewCardProps) {
  return (
    <Card className={variant === 'landscape' ? 'mb-6' : ''}>
      <CardHeader>
        <CardTitle>Product Preview</CardTitle>
        <CardDescription>Main product image</CardDescription>
      </CardHeader>
      <CardContent>
        {previewImage ? (
          <div
            className={`w-full overflow-hidden rounded-lg border cursor-pointer group relative bg-muted ${
              variant === 'portrait' ? 'aspect-[3/4]' : ''
            }`}
            onClick={onOpenLightbox}
          >
            <img
              src={previewImage}
              alt={productTitle}
              className={`w-full transition-transform group-hover:scale-105 ${
                variant === 'portrait' ? 'h-full object-contain' : 'h-auto'
              }`}
            />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <Maximize2 className="h-8 w-8 text-white" />
            </div>
          </div>
        ) : (
          <div className={`w-full bg-muted rounded-lg flex items-center justify-center ${
            variant === 'portrait' ? 'aspect-[3/4]' : 'aspect-[4/3]'
          }`}>
            <p className="text-muted-foreground">No preview available</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface CustomerFilesCardProps {
  googleDriveLink: string | null;
  copiedLink: boolean;
  onCopyLink: () => void;
}

function CustomerFilesCard({ googleDriveLink, copiedLink, onCopyLink }: CustomerFilesCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Customer Files</CardTitle>
        <CardDescription>Google Drive folder with all print files</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {googleDriveLink ? (
          <>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => window.open(googleDriveLink, '_blank')}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Drive Folder
              </Button>
              <Button variant="outline" size="icon" onClick={onCopyLink}>
                {copiedLink ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground break-all">{googleDriveLink}</p>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">No Drive link available</p>
        )}
      </CardContent>
    </Card>
  );
}

interface DeliverablePdfCardProps {
  finalPdfUrl: string | null;
}

function DeliverablePdfCard({ finalPdfUrl }: DeliverablePdfCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Deliverable PDF</CardTitle>
        <CardDescription>Customer-facing PDF with instructions</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {finalPdfUrl ? (
          <>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => window.open(finalPdfUrl, '_blank')}
            >
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
            <p className="text-xs text-muted-foreground break-all">{finalPdfUrl}</p>
          </>
        ) : (
          <>
            <Button variant="outline" className="w-full" disabled>
              <Download className="h-4 w-4 mr-2" />
              Not Available
            </Button>
            <p className="text-xs text-muted-foreground">PDF generation coming soon</p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

interface ActionsCardProps {
  onRedoCrop?: () => void;
  hasMasterImage: boolean;
  onRename: () => void;
  onDelete: () => void;
  actionError: string | null;
}

function ActionsCard({ onRedoCrop, hasMasterImage, onRename, onDelete, actionError }: ActionsCardProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Actions</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {onRedoCrop && hasMasterImage && (
          <Button variant="outline" className="w-full" onClick={onRedoCrop}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Redo Crops
          </Button>
        )}
        <Button variant="outline" className="w-full" disabled>
          Export to CSV
        </Button>
        <Button variant="outline" className="w-full" onClick={onRename}>
          Rename Product
        </Button>
        <Button variant="destructive" className="w-full" onClick={onDelete}>
          Delete Product
        </Button>
        {actionError && <p className="mt-1 text-xs text-destructive">{actionError}</p>}
      </CardContent>
    </Card>
  );
}
